import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import JoditEditor from "jodit-react";
import { ProtectAPi } from '../../../api/ProtectAPi';
import swal from 'sweetalert';
import { ServerUrl } from '../../../api/ServerUrl';
import BackButton from '../../tools/BackButton';
import AdminQuickNav from '../landingPage/AdminQuickNav';
import { v4 as uuidv4 } from 'uuid';

/* ================= SIZE MULTI SELECT (SAME AS ADD) ================= */
const SizeMultiSelect = ({ options, value = [], onChange, placeholder = 'Select sizes' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSize = (s) => {
    const sStr = String(s);
    const exists = value.includes(sStr);
    const next = exists ? value.filter(v => v !== sStr) : [...value, sStr];
    onChange(next);
  };

  const removeChip = (s) => {
    onChange(value.filter(v => v !== String(s)));
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        role="button"
        onClick={() => setOpen(o => !o)}
        className="form-control"
        style={{
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          cursor: 'text',
          padding: '6px 8px'
        }}
      >
        {value.length === 0 ? (
          <div style={{ color: '#6c757d' }}>{placeholder}</div>
        ) : (
          value.map(v => (
            <div key={v} style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#f1f3f5',
              border: '1px solid #e2e6ea',
              padding: '4px 8px',
              borderRadius: 6,
              marginRight: 6,
            }}>
              <span style={{ marginRight: 6 }}>{v}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeChip(v); }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  color: '#333',
                  fontWeight: 700,
                  lineHeight: 1
                }}
                aria-label={`Remove ${v}`}
              >×</button>
            </div>
          ))
        )}

        <div style={{ marginLeft: 'auto', opacity: 0.7 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d={open ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} stroke="#444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          zIndex: 999,
          top: 'calc(100% + 6px)',
          left: 0,
          width: '100%',
          maxHeight: 220,
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
          borderRadius: 6,
          padding: 8
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {options.map(opt => {
              const selected = value.includes(String(opt));
              return (
                <button
                  type="button"
                  key={opt}
                  onClick={() => toggleSize(opt)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: selected ? '1px solid #3b82f6' : '1px solid #d1d5db',
                    background: selected ? '#eff6ff' : '#fff',
                    cursor: 'pointer'
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= LABEL ================= */
const Label = ({ children, required }) => (
  <label>
    {children} {required && <span style={{ color: 'red' }}>*</span>}
  </label>
);

/* ================= MAIN ================= */
const LandingProductEdit = () => {
  const { id } = useParams();
  const httpCPage = ServerUrl;

  const handleCareguideFile = (e) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setCareguideFile(f);
  };

  const handleSizeguideFile = (e) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setSizeguideFile(f);
  };

  const { http } = useMemo(() => ProtectAPi(), []);
  const navigate = useNavigate();
  const editor = useRef(null);

  const [loader, setLoader] = useState('off');
  const [errors, setErrors] = useState({});
  const [layoutImageErrors, setLayoutImageErrors] = useState([]);

  /* ================= STATES (SAME AS ADD) ================= */
  const [productName, setProductName] = useState('');
  const [articleNumber, setArticleNumber] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [productSlug, setProductSlug] = useState('');
  const [layoutDescription, setLayoutDescription] = useState('');
  const [price, setPrice] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState(1);
  const [removedImages, setRemovedImages] = useState([]);      
  const [removedColorImages, setRemovedColorImages] = useState([]);


  const [careguideFile, setCareguideFile] = useState(null);
  const [sizeguideFile, setSizeguideFile] = useState(null);

  const [selectedSizes, setSelectedSizes] = useState([]);
  const [layoutImages, setLayoutImages] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [colors, setColors] = useState([]);

  const sizeOptions = [
    'S',
    'M',
    'L',
    'XL',
    ...Array.from({ length: 12 }, (_, i) => 34 + i), // 34..45
  ];




  /* ================= LOAD COLORS ================= */
  useEffect(() => {
    http.get('/get-landing-product-color').then(res => {
      const list = Array.isArray(res.data) ? res.data : res.data.data;
      setColorOptions(list || []);
    });
  }, []);

  /* ================= LOAD PRODUCT (EDIT ONLY) ================= */
  useEffect(() => {
    http.get(`/get-single-landing-product/${id}`)
      .then(res => {

        // 🔥 SUPPORT BOTH RESPONSE TYPES
        const p = res.data?.product ?? res.data;

        if (!p) {
          console.error('Product data not found', res.data);
          return;
        }

        setProductName(p.product_name || '');
        setArticleNumber(p.article_number || '');
        setShortDescription(p.short_description || '');
        setProductSlug(p.slug || '');
        setLayoutDescription(p.description || '');
        setPrice(p.price || '');
        setPriority(p.priority || '');
        setStatus(p.status ?? 1);
        setSelectedSizes(p.size || []);

        /* GENERAL IMAGES */
        if (Array.isArray(p.images)) {
          setLayoutImages(p.images.map(img => ({
            id: img.id,
            file: null,
            preview: img.image,
            alt: img.alt_text || '',
            colorId: img.color_id || ''
          })));
        }

        /* COLOR + COLOR IMAGES */
        if (Array.isArray(p.color_imgs)) {
          const grouped = {};
          p.color_imgs.forEach(ci => {
            const colorInfo = (p.colors || []).find(c => c.id === ci.colorid);
            if (!grouped[ci.colorid]) {
              grouped[ci.colorid] = {
                tmp_key: uuidv4(),
                colorId: ci.colorid,
                colorCode: colorInfo?.color_code || '',
                files: []
              };
            }
            grouped[ci.colorid].files.push({
              id: ci.id,
              file: null,
              preview: ci.image
            });
          });
          setColors(Object.values(grouped));
        }

      })
      .catch(err => {
        console.error('Fetch product error', err);
      });
  }, [id]);

  /* ================= IMAGE HANDLERS (SAME AS ADD) ================= */
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    setLayoutImages(prev => [
      ...prev,
      ...files.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        alt: '',
        colorId: ''
      }))
    ]);
  };

  const handleAltChange = (i, v) => {
    setLayoutImages(p => {
      const c = [...p];
      c[i].alt = v;
      return c;
    });
  };

  const handleImageColorChange = (i, v) => {
    setLayoutImages(p => {
      const c = [...p];
      c[i].colorId = v;
      return c;
    });
  };




  const handleImageRemove = (i) => {
    setLayoutImages(prev => {
      const img = prev[i];
      if (img?.id) {
        setRemovedImages(r => [...r, img.id]);
      }
      return prev.filter((_, x) => x !== i);
    });
  };





  /* ================= COLOR HANDLERS ================= */
  const addColor = () => {
    setColors(p => [...p, { tmp_key: uuidv4(), colorId: '', colorCode: '', files: [] }]);
  };

const updateColorSelect = (i, v) => {
  const opt = colorOptions.find(o => String(o.id) === String(v)) || {};
  setColors(p => {
    const c = [...p];
    c[i].colorId = v;
    c[i].colorCode = opt.color_code || '';
    c[i].files = []; // 🔥 clear old previews
    return c;
  });
};




  const handleColorFilesChange = (i, e) => {
    const files = Array.from(e.target.files || []).map(f => ({
      file: f,
      preview: URL.createObjectURL(f)
    }));

    setColors(prev => {
      const c = [...prev];

      // 🔥 STEP 1: old image id collect (for backend delete)
      const oldFiles = c[i].files || [];
      oldFiles.forEach(f => {
        if (f?.id) {
          setRemovedColorImages(r => [...r, f.id]);
        }
      });

      // 🔥 STEP 2: replace files (NOT append)
      c[i].files = files;

      return c;
    });

    // 🔥 reset input
    e.target.value = null;
  };





  const removeColorFile = (ci, fi) => {
    setColors(prev => {
      const c = [...prev];
      const file = c[ci].files[fi];

      // 🔥 existing color image হলে id store
      if (file?.id) {
        setRemovedColorImages(r => [...r, file.id]);
      }

      c[ci].files.splice(fi, 1);
      return c;
    });
  };

 const removeColor = (i) => {
  setColors(prev => {
    const c = [...prev];

    // 🔥 collect all existing color image ids in this row
    const row = c[i];
    if (row?.files?.length) {
      row.files.forEach(f => {
        if (f?.id) {
          setRemovedColorImages(r => [...r, f.id]);
        }
      });
    }

    // remove the row
    c.splice(i, 1);
    return c;
  });
};


  /* ================= SUBMIT ================= */
  const submitForm = async () => {
    setLoader('on');
    setErrors({});

    const fd = new FormData();
    fd.append('_method', 'PUT');
    fd.append('product_name', productName);
    fd.append('article_number', articleNumber);
    fd.append('short_description', shortDescription);
    fd.append('slug', productSlug);
    fd.append('description', layoutDescription);
    fd.append('price', price);
    fd.append('priority', priority);
    fd.append('status', status);
    if (careguideFile) fd.append('careguide', careguideFile);
    if (sizeguideFile) fd.append('sizeguide', sizeguideFile);
    removedImages.forEach(id => {
      fd.append('removed_images[]', id);
    });

    // 🔥 send removed color image ids
    removedColorImages.forEach(id => {
      fd.append('removed_color_images[]', id);
    });

    selectedSizes.forEach(s => fd.append('size[]', s));


  let newImgIndex = 0;
  layoutImages.forEach((img) => {
    if (img.file && img.file.name) {
      fd.append('images[]', img.file);
      fd.append(`alt_texts[${newImgIndex}]`, img.alt || '');
      fd.append(`image_color_ids[${newImgIndex}]`, Number(img.colorId) || '');
      newImgIndex++;
    }
  });


  colors.forEach(c => {
    c.files.forEach(f => {
      if (f.file && f.file.name && c.colorId) {
        fd.append('color_images[]', f.file);
        fd.append('color_image_color_ids[]', Number(c.colorId));
      }
    });
  });

    try {
      await http.post(`/update-landing-product/${id}`, fd);
      swal('Success', 'Product updated successfully', 'success');
      navigate('/admin/landing-page-products');
    } catch (e) {
      setErrors(e.response?.data?.errors || {});
      swal('Error', 'Update failed', 'error');
    } finally {
      setLoader('off');
    }
  };

  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 300,
    uploader: { insertImageAsBase64URI: true }
  }), []);

  /* ================= UI (100% SAME AS ADD) ================= */
  return (
   <div className="main-content" style={{ marginTop: -20 }}>
      <section className="section">
        <div className="section-body">
          <AdminQuickNav active="products" />
          <div className="card card-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <h5>Edit Product</h5>
                <BackButton />
              </div>

              {/* General images */}
              <div className="form-group mt-4">
                <Label required>Multiple Product Images</Label>
                <input type="file" className="form-control" multiple onChange={handleImageChange} />
                {errors.images && <div className="text-danger mt-1">{errors.images}</div>}
                {layoutImageErrors.length > 0 && layoutImageErrors.map((err, i) => (
                  <span key={i} className="text-danger d-block">{err}</span>
                ))}
              </div>

              {/* preview general images with alt + color select */}
              {layoutImages.length > 0 && (
                <div className="row mt-3">
                  {layoutImages.map((img, index) => (
                    <div className="col-md-3 mb-4" key={index}>
                      <div className="border p-2 rounded shadow-sm">
                        <img
                          src={img.file ? img.preview : httpCPage + img.preview}
                          alt={img.alt}
                          className="img-fluid mb-2"
                          style={{ height: '150px', width: '100%' }}
                        />

                        <input
                          type="text"
                          className="form-control mb-2"
                          placeholder="Alt text (e.g. front view)"
                          value={img.alt}
                          onChange={e => handleAltChange(index, e.target.value)}
                        />
                        <select className="form-control mb-2" value={img.colorId || ''} onChange={e => handleImageColorChange(index, e.target.value)}>
                          <option value="">Select Color (optional)</option>
                          {colorOptions.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.color_name || c.name || String(c.id)}
                            </option>
                          ))}
                        </select>
                        <button type="button" className="btn btn-danger btn-md w-100 mt-2" onClick={() => handleImageRemove(index)}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}


              <div className="form-row">

                <div className="form-group col-md-6">
                  <Label required>Product Name</Label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter product name"
                    value={productName}
                    onChange={e => setProductName(e.target.value)}
                  />
                  {errors.product_name && <span className="text-danger">{errors.product_name}</span>}
                </div>

                <div className="form-group col-md-6">
                  <Label required>Article Number</Label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Article Number"
                    value={articleNumber}
                    onChange={e => setArticleNumber(e.target.value)}
                  />
                  {errors.article_number && <span className="text-danger">{errors.article_number}</span>}
                </div>

                <div className="form-group col-md-12">
                  <Label>Short Description</Label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Short description (one-liner)"
                    value={shortDescription}
                    onChange={e => setShortDescription(e.target.value)}
                  />
                  {errors.short_description && <span className="text-danger">{errors.short_description}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-3">
                  <Label>Slug (optional)</Label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Slug"
                    value={productSlug}
                    onChange={e => setProductSlug(e.target.value)}
                  />
                  {errors.slug && <span className="text-danger">{errors.slug}</span>}
                </div>
                <div className="form-group col-md-3">
                  <Label required>Price</Label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    placeholder="Enter Price in BDT"
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                  />
                  {errors.price && <span className="text-danger">{errors.price}</span>}
                </div>
                <div className="form-group col-md-3">
                  <Label>Priority (optional)</Label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter Priority (optional)"
                    value={priority}
                    onChange={e => setPriority(e.target.value)}
                  />
                  {errors.priority && <span className="text-danger">{errors.priority}</span>}

                </div>
                <div className="form-group col-md-3">
                  <Label required>Status</Label>
                  <select
                    name="status"
                    className="form-control"
                    value={status}
                    onChange={e => setStatus(e.target.value)}
                  >
                    <option value="1">Active</option>
                    <option value="2">Deactive</option>
                  </select>

                  {errors.status && (
                    <span className="text-danger">{errors.status}</span>
                  )}
                </div>

              </div>

              <div className="form-group">
                <Label required>Sizes</Label>
                <SizeMultiSelect
                  options={sizeOptions}
                  value={selectedSizes}
                  onChange={(next) => setSelectedSizes(next)}
                  placeholder="Select sizes (click to open)"
                />
                {errors.size && <div className="text-danger">{errors.size}</div>}
              </div>


              {/* careguide & sizeguide files (optional) */}
              <div className="form-row">
                <div className="form-group col-md-6">
                  <Label>Care Guide (file) <small className="text-muted">(optional)</small></Label>
                  <input type="file" className="form-control" onChange={handleCareguideFile} />
                  {errors.careguide && <div className="text-danger">{errors.careguide}</div>}
                </div>
                <div className="form-group col-md-6">
                  <Label>Size Guide (file) <small className="text-muted">(optional)</small></Label>
                  <input type="file" className="form-control" onChange={handleSizeguideFile} />
                  {errors.sizeguide && <div className="text-danger">{errors.sizeguide}</div>}
                </div>
              </div>

              {/* colors table */}
              <div className="mb-3 border rounded p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6>Select Color & Color Image</h6>
                  <button type="button" className="btn btn-md mb-2 btn-primary" onClick={addColor}>Add Color Row</button>
                </div>

                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th style={{ width: '25%' }}>Select Color</th>
                        <th style={{ width: '25%' }}>Color Code</th>
                        <th style={{ width: '30%' }}>Upload Color Images</th>
                        <th className='text-center' style={{ width: '10%' }}> Preview</th>

                        <th style={{ width: '10%' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {colors.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center">No color rows. Click "Add Color Row" to add.</td>
                        </tr>
                      )}
                      {colors.map((c, idx) => (
                        <tr key={c.tmp_key}>
                          <td>
                            <select className="form-control" value={c.colorId || ''} onChange={e => updateColorSelect(idx, e.target.value)}>
                              <option value="">Choose Color</option>
                              {colorOptions.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.color_name || opt.name || `#${opt.id}`}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input className="form-control" value={c.colorCode || ''} readOnly placeholder="Auto-filled code" />
                          </td>
                          <td>
                            <input type="file" className="form-control" multiple onChange={e => handleColorFilesChange(idx, e)} />
                          </td>
                          <td className="text-center align-middle">
                            {(c.files || []).length > 0 && (
                              <div className="d-flex justify-content-center align-items-center flex-wrap mt-2">
                                {(c.files || []).map((f, fi) => (
                                  <div
                                    key={fi}
                                    className="d-flex justify-content-center align-items-center me-2"
                                  >
                                    <img
                                      src={f.file ? f.preview : httpCPage + f.preview}
                                      alt="preview"
                                      style={{ width: '60px', height: '60px' }}
                                    />

                                  </div>
                                ))}
                              </div>
                            )}
                          </td>

                          <td className="text-center">
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => removeColor(idx)}>Remove</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* description (optional) */}
              <div className="form-group">
                <Label>Description <small className="text-muted">(optional)</small></Label>
                <JoditEditor
                  ref={editor}
                  value={layoutDescription}
                  config={editorConfig}
                  tabIndex={1}
                  onBlur={newContent => setLayoutDescription(newContent)}
                  onChange={() => { }}
                />
                {errors.description && <span className="text-danger">{errors.description}</span>}
              </div>


              {/* SUBMIT */}
              <div className="mt-4">
                {loader === 'off' ? (
                  <button className="btn btn-primary btn-lg" onClick={submitForm}>
                    Update Product
                  </button>
                ) : (
                  <button className="btn btn-primary btn-lg" disabled>
                    <span className="spinner-border spinner-border-sm mr-2" />
                    Updating...
                  </button>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingProductEdit;
